import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill-new';

import IconError from '../IconError';

import 'react-quill-new/dist/quill.snow.css';
import common from './formElementStyles/commonFormElements.module.css';
import rte from './formElementStyles/rte.module.css';

Quill.register('modules/maxLength', function (quill, options) {
    quill.on('text-change', () => {
        const textLength = quill.getLength() - 1;
        if (textLength > options.limit) {
            quill.deleteText(options.limit, textLength - options.limit);
        }
    });
});

const defaultModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        ['clean']
    ]
};

const RichTextEditor = ({
    id = '',
    label = '',
    placeholder = '',
    mandatory = false,
    disabled = false,
    limit,
    isCount = false,
    value,
    customClass = '',
    customLabelClass = '',
    error = {},
    onChange
}) => {
    const quillRef = useRef(null);

    // LOCAL STATE
    const [modules, setModules] = useState(defaultModules);
    const [count, setCount] = useState(0);

    // USEEFFECTS
    useEffect(() => {
        if (quillRef.current) {
            const root = quillRef.current.getEditor().root;
            if (root) root.setAttribute('aria-labelledby', `input-${id}`);
        }
    }, []);

    useEffect(() => {
        if (!limit) return;
        setModules((prev) => ({ ...prev, maxLength: { limit } }));
    }, [limit]);

    // EVENT HANDLERS
    const onContentChange = (content, delta, source, editor) => {
        if (isCount) setCount(editor.getLength() - 1);
        onChange(content);
    };

    // RENDER
    return (
        <div id={id} className={`${rte.wrapper} ${customClass ? common[customClass] : ''}`}>
            <label
                id={`label-${id}`}
                htmlFor={`input-${id}`}
                className={`${common.formLabel} ${customLabelClass ? common[customLabelClass] : ''}`}>
                <span>
                    {label} {mandatory && <sup>*</sup>}
                </span>
            </label>
            <div className={rte.textBox} aria-labelledby={`label-${id}`}>
                <ReactQuill
                    id={`input-${id}`}
                    ref={quillRef}
                    value={value}
                    modules={modules}
                    placeholder={placeholder}
                    readOnly={disabled}
                    style={{ color: disabled ? '#b2b2b2' : '#000' }}
                    onChange={onContentChange}
                />
            </div>
            <div className={rte.baseContent}>
                {isCount && (
                    <div aria-label="character count" data-testid={`${id}-count`}>
                        {limit ? `${count} / ${limit}` : `${count}`}
                    </div>
                )}
                {error?.error && (
                    <div className={rte.error}>
                        <IconError text={error} />
                    </div>
                )}
            </div>
        </div>
    );
};

RichTextEditor.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    limit: PropTypes.number,
    isCount: PropTypes.bool,
    mandatory: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customClass: PropTypes.string,
    customLabelClass: PropTypes.string,
    error: PropTypes.object,
    onChange: PropTypes.func
};

export default RichTextEditor;
